import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, Tag, List, Button, Space, Typography, Divider, Alert, Tabs, 
  Row, Col, message, Modal, Descriptions
} from 'antd';
import { 
  DownloadOutlined, FileTextOutlined, SafetyOutlined, 
  CheckOutlined, HistoryOutlined 
} from '@ant-design/icons';
import api from '../services/api';
import VersionHistory from '../components/VersionHistory';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

interface RiskItem {
  type: string;
  original_clause: string;
  suggested_clause: string;
  description: string;
  risk_level: string;
  law: string;
  position: string;
  suggestion?: string;  // 兼容旧格式
}

interface AuditResult {
  contract_id: string;
  risk_items: RiskItem[];
  summary: string;
  suggestions: string[];
}

const AuditResultPage: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const [result, setResult] = useState<AuditResult | null>(null);
  const [contract, setContract] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  useEffect(() => {
    fetchData();
  }, [contractId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const contractResponse = await api.get(`/contracts/${contractId}`);
      setContract(contractResponse.data);
      
      const resultResponse = await api.get(`/audit/${contractId}/result`);
      setResult(resultResponse.data);
    } catch (error) {
      console.error('获取审核结果失败:', error);
      message.error('获取审核结果失败');
    } finally {
      setLoading(false);
    }
  };

  const getRiskTagColor = (level: string) => {
    switch (level) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'blue';
      default: return 'default';
    }
  };

  const getRiskText = (level: string) => {
    switch (level) {
      case 'high': return '高风险';
      case 'medium': return '中风险';
      case 'low': return '低风险';
      default: return level;
    }
  };

  // 采纳单条建议
 const handleAcceptSuggestion = async (item: RiskItem) => {
  setAccepting(true);
  try {
    const response = await api.post('/contract/accept-suggestion', {
      contract_id: contractId,
      original_clause: item.original_clause,
      suggested_clause: item.suggested_clause,
      risk_type: item.type
    });
    message.success(response.data.message || '建议已采纳，合同已更新');
    await fetchData();
 } catch (error: any) {
  console.error('采纳失败:', error);
  message.error(error.response?.data?.detail || '采纳失败，请重试');
} finally {
    setAccepting(false);
  }
};

  // 一键采纳所有建议
 const handleAcceptAll = async () => {
  if (!result?.risk_items) return;
  
  Modal.confirm({
    title: '确认采纳所有建议',
    content: `将采纳 ${result.risk_items.length} 条修改建议，合同将被更新。是否继续？`,
    onOk: async () => {
      setAccepting(true);
      try {
        const suggestions = result.risk_items.map(item => ({
          original_clause: item.original_clause,
          suggested_clause: item.suggested_clause
        }));
        
        // 正确的方式：第二个参数是 data，第三个参数是 config
        const response = await api.post('/contract/accept-all-suggestions', {
          contract_id: contractId,
          suggestions: suggestions
        });
        
        message.success(response.data.message || '已采纳所有建议，合同已更新');
        await fetchData();
      } catch (error: any) {
  console.error('采纳失败:', error);
  message.error(error.response?.data?.detail || '采纳失败，请重试');
} finally {
        setAccepting(false);
      }
    }
  });
};

  // 导出报告
  const handleExport = async () => {
  if (!contractId) return;
  try {
    const response = await api.get(`/audit/${contractId}/export`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `审核报告_${contract?.title || '合同'}.docx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    message.success('报告下载成功');
  } catch (error) {
    console.error('导出失败:', error);
    message.error('导出失败，请重试');
  }
};

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 50 }}>加载中...</div>;
  }

  if (!result) {
    return <Alert message="未找到审核结果" type="error" />;
  }

  // 兼容旧格式：如果没有 original_clause，使用 description 和 suggestion
  const riskItems = result.risk_items.map(item => ({
    ...item,
    original_clause: item.original_clause || item.description,
    suggested_clause: item.suggested_clause || item.suggestion || ''
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>合同审核报告</Title>
        <Space>
          <Button 
            icon={<HistoryOutlined />} 
            onClick={() => setShowVersionHistory(true)}
          >
            版本历史
          </Button>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={handleExport}
          >
            导出报告
          </Button>
        </Space>
      </div>
      
      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="合同名称">{contract?.title}</Descriptions.Item>
          <Descriptions.Item label="审核时间">{new Date().toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="风险等级">
            <Tag color={contract?.risk_level === '高风险' ? 'red' : contract?.risk_level === '中风险' ? 'orange' : 'blue'}>
              {contract?.risk_level || '待审核'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="风险评分">{contract?.risk_score}分</Descriptions.Item>
        </Descriptions>
      </Card>

      <Alert
        message="审核摘要"
        description={result.summary}
        type={contract?.risk_level === '高风险' ? 'error' : contract?.risk_level === '中风险' ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Tabs defaultActiveKey="risks">
        <TabPane
          tab={
            <span>
              <SafetyOutlined />
              风险点详情 ({riskItems.length})
            </span>
          }
          key="risks"
        >
          <List
            itemLayout="vertical"
            dataSource={riskItems}
            renderItem={(item, idx) => (
              <List.Item>
                <Card 
                  type="inner" 
                  style={{ 
                    borderLeft: `4px solid ${item.risk_level === 'high' ? '#f5222d' : item.risk_level === 'medium' ? '#fa8c16' : '#1890ff'}`,
                    marginBottom: 16
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <Tag color={getRiskTagColor(item.risk_level)}>
                      {getRiskText(item.risk_level)}
                    </Tag>
                    <Text strong style={{ fontSize: 16, marginLeft: 8 }}>{item.type}</Text>
                    {item.position && (
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        位置：{item.position}
                      </Text>
                    )}
                  </div>
                  
                  {/* 并排比对：原条款 vs 建议条款 */}
                  <Row gutter={16} style={{ marginTop: 12 }}>
                    <Col span={12}>
                      <Card 
                        size="small" 
                        title="原条款" 
                        style={{ background: '#fff2e8', height: '100%' }}
                        headStyle={{ background: '#fff2e8' }}
                      >
                        <Paragraph 
                          copyable 
                          style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
                        >
                          {item.original_clause || '未定位到具体条款'}
                        </Paragraph>
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card 
                        size="small" 
                        title="建议条款" 
                        style={{ background: '#f6ffed', height: '100%' }}
                        headStyle={{ background: '#f6ffed' }}
                      >
                        <Paragraph 
                          copyable 
                          style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
                        >
                          {item.suggested_clause || item.suggestion || '建议参考上述修改建议'}
                        </Paragraph>
                      </Card>
                    </Col>
                  </Row>
                  
                  {/* 问题描述和法律依据 */}
                  <div style={{ marginTop: 16 }}>
                    <Paragraph>
                      <Text type="secondary">问题描述：</Text>
                      <br />
                      {item.description}
                    </Paragraph>
                    {item.law && (
                      <Paragraph>
                        <Text type="secondary">法律依据：</Text>
                        <br />
                        <Text code>{item.law}</Text>
                      </Paragraph>
                    )}
                  </div>
                  
                  {/* 操作按钮 */}
                  <Space style={{ marginTop: 12 }}>
                    <Button 
                      type="primary" 
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={() => handleAcceptSuggestion(item)}
                      loading={accepting}
                      disabled={!item.suggested_clause}
                    >
                      采纳此建议
                    </Button>
                    <Button size="small">忽略</Button>
                  </Space>
                </Card>
              </List.Item>
            )}
          />
          
          {/* 一键采纳所有建议按钮 */}
          {riskItems.length > 0 && (
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button 
                type="primary" 
                size="large"
                icon={<CheckOutlined />}
                onClick={handleAcceptAll}
                loading={accepting}
              >
                一键采纳所有建议 ({riskItems.length}条)
              </Button>
            </div>
          )}
        </TabPane>
        
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              修改建议汇总
            </span>
          }
          key="suggestions"
        >
          <List
            dataSource={result.suggestions}
            renderItem={(item, index) => (
              <List.Item>
                <Card style={{ width: '100%', background: '#fafafa' }}>
                  <Space>
                    <Text strong>{index + 1}.</Text>
                    <Text>{item}</Text>
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        </TabPane>
      </Tabs>

      <Divider />

      <Space>
        <Button onClick={() => navigate('/contracts')}>返回列表</Button>
      </Space>

      {/* 版本历史弹窗 */}
      <Modal
        title="版本历史"
        open={showVersionHistory}
        onCancel={() => setShowVersionHistory(false)}
        footer={null}
        width={800}
      >
        {contractId && <VersionHistory contractId={contractId} />}
      </Modal>
    </div>
  );
};

export default AuditResultPage;